import { Svgs } from '../../../assets/svg';
import { Report, BreakdownReport, SummedReport } from '../../../models/ReportData';
import { getProductTypeKey, findPricebookReportsByType } from './pricebookReportUtils';
import { ProductTypeEnum } from '../../../models/PricebookData';
import {
  calculateUnknownAccountCounts,
  calculatePricebookAccountCounts,
  sumAccountCountsByStatus,
} from './calculationUtils';
import { formatNumber } from '../../../helpers/utilityFunctions';
import { TypographyProps } from '@mui/material';
import React from 'react';

// ───────────────────────────── Types & Constants ─────────────────────────────
interface AccountCounts {
  activeUserCount: number;
  ineligibleCount: number;
  freeTrialPaymentPlatformCount: number;
  unsubscribedCount: number;
  abandonedCount: number;
  invitedCount: number;
  undecidedCount: number;
  activeAddOnCount: number;
  unsubscribedFromAddOnCount: number;
}

enum AccountStatusLabel {
  activeUserCount = 'Active',
  invitedCount = 'Invited',
  undecidedCount = 'Undecided',
  abandonedCount = 'Abandoned',
  ineligibleCount = 'Ineligible',
  freeTrialPaymentPlatformCount = 'In trial period',
  unsubscribedCount = 'Unsubscribed',
  activeAddOnCount = 'Upgraded to add-on',
  unsubscribedFromAddOnCount = 'Unsubscribed from add-on',
}

type AccountStatusKeys = keyof typeof AccountStatusLabel;

interface UserAccountsTableData {
  [key: string]: AccountCounts;
}

const ACTIVE_SUB_ROWS: AccountStatusKeys[] = [
  'freeTrialPaymentPlatformCount',
  'activeAddOnCount',
  'unsubscribedFromAddOnCount',
];

const ACTIVE_SUB_ROWS_HEADER = 'Included in active user count';

const ALL_COLUMN_TYPES = [
  'Total',
  '3Nickels',
  'GuidedChoice',
  'Bundled',
  'Free',
  'Unknown',
] as const;

// ───────────────────────────── Table Functions ─────────────────────────────
const formatAccountCount = (value: number | null | undefined): string => {
  return value === null || value === undefined ? '-' : formatNumber(value);
};

const buildAccountsTable = (report: Report): UserAccountsTableData => {
  const tableData: UserAccountsTableData = {};
  const allPricebookReports = findPricebookReportsByType(report as SummedReport);

  allPricebookReports.forEach(pricebookReport => {
    if (!pricebookReport.productType) return;

    if (pricebookReport.productType === ProductTypeEnum.Total) {
      tableData['Total'] = {
        ...calculatePricebookAccountCounts(pricebookReport),
        abandonedCount: report.abandonedCount || 0,
        invitedCount: report.invitedCount || 0,
        undecidedCount: report.undecidedCount || 0,
      };
    } else {
      const productTypeKey = getProductTypeKey(pricebookReport.productType);
      tableData[productTypeKey] = calculatePricebookAccountCounts(pricebookReport);
    }
  });

  tableData['Unknown'] = calculateUnknownAccountCounts(report);

  return tableData;
};

function getVisibleColumnTypes(data: UserAccountsTableData): string[] {
  if (Object.keys(data).length === 0) return [];
  return ALL_COLUMN_TYPES.filter(col => col === 'Total' || col === 'Unknown' || col in data);
}

function getBreakdownRowKeys(): AccountStatusKeys[] {
  return (Object.keys(AccountStatusLabel) as AccountStatusKeys[]).filter(
    key => !ACTIVE_SUB_ROWS.includes(key),
  );
}

function getAccountStatusIcon(accountStatus: AccountStatusLabel) {
  switch (accountStatus) {
    case AccountStatusLabel.activeUserCount:
      return <Svgs.IconStatusActive />;
    case AccountStatusLabel.abandonedCount:
      return <Svgs.IconStatusAbandoned />;
    case AccountStatusLabel.invitedCount:
      return <Svgs.IconStatusInvited />;
    case AccountStatusLabel.ineligibleCount:
      return <Svgs.IconStatusIneligible />;
    case AccountStatusLabel.freeTrialPaymentPlatformCount:
      return <Svgs.IconStatusTrial />;
    case AccountStatusLabel.undecidedCount:
      return <Svgs.IconStatusUndecided />;
    case AccountStatusLabel.unsubscribedCount:
      return <Svgs.IconStatusUnsubscribed />;
    case AccountStatusLabel.activeAddOnCount:
      return <Svgs.IconStatusUpgraded />;
    case AccountStatusLabel.unsubscribedFromAddOnCount:
      return <Svgs.IconStatusUnsubscribed />;
    default:
      return null;
  }
}

// ───────────────────────────── Report Functions ─────────────────────────────
function getNestedReports(breakdownReport: BreakdownReport): Report[] {
  if (
    'organizationReports' in breakdownReport &&
    Array.isArray(breakdownReport.organizationReports)
  ) {
    return breakdownReport.organizationReports;
  } else if ('teamReports' in breakdownReport && Array.isArray(breakdownReport.teamReports)) {
    return breakdownReport.teamReports;
  } else {
    return [breakdownReport];
  }
}

function sortReportsAlphabetically(reports: Report[]): Report[] {
  return reports.sort((a, b) => {
    const nameA = 'name' in a && typeof a.name === 'string' ? a.name : '';
    const nameB = 'name' in b && typeof b.name === 'string' ? b.name : '';
    return nameA.localeCompare(nameB);
  });
}

// ───────────────────────────── Section Functions ─────────────────────────────
export interface UserAccountSection {
  label: string;
  rows: AccountStatusKeys[];
  count: number;
  countVariant?: TypographyProps['variant'];
  helpContent?: React.ReactNode;
}

const buildUserAccountSections = (data: UserAccountsTableData): UserAccountSection[] => {
  if (!data.Total) return [];

  const preSubscriptionRows: AccountStatusKeys[] = [
    'invitedCount',
    'undecidedCount',
    'abandonedCount',
  ];
  const postSubscriptionRows: AccountStatusKeys[] = ['ineligibleCount', 'unsubscribedCount'];

  return [
    {
      label: 'Active',
      rows: ['activeUserCount' as const],
      count: data.Total.activeUserCount || 0,
      countVariant: 'p30',
    },
    {
      label: 'Pre-subscription',
      rows: preSubscriptionRows,
      count: sumAccountCountsByStatus(data, preSubscriptionRows),
      countVariant: 'p24',
    },
    {
      label: 'Post-subscription',
      rows: postSubscriptionRows,
      count: sumAccountCountsByStatus(data, postSubscriptionRows),
      countVariant: 'p24',
    },
  ];
};

export {
  type AccountCounts,
  type AccountStatusKeys,
  type UserAccountsTableData,
  AccountStatusLabel,
  ACTIVE_SUB_ROWS,
  ACTIVE_SUB_ROWS_HEADER,
  ALL_COLUMN_TYPES,
  buildAccountsTable,
  getVisibleColumnTypes,
  getBreakdownRowKeys,
  getAccountStatusIcon,
  getNestedReports,
  sortReportsAlphabetically,
  buildUserAccountSections,
  formatAccountCount,
};
